import { useEffect, useRef, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RefreshCw, Upload, Plus, X, Pencil } from "lucide-react";
import { toast } from "sonner";

const fmt0 = (n) => n === undefined || n === null ? "—"
  : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);
const wkLabel = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};

export default function CashFlowPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cfgEdit, setCfgEdit] = useState(null);      // {opening_cheque, opening_term, min_cash}
  const [cellEdit, setCellEdit] = useState(null);    // {week, item_id|null(=AR), value}
  const [itemModal, setItemModal] = useState(null);  // item form
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/finance/cashflow");
      setData(d);
    } catch (e) { toast.error(e.response?.data?.detail || "Cash flow load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const saveCfg = async () => {
    try {
      await api.put("/finance/cashflow/config", cfgEdit);
      toast.success("Opening balances updated");
      setCfgEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const saveCell = async () => {
    if (!cellEdit) return;
    const amount = cellEdit.value === "" ? null : Number(cellEdit.value);
    try {
      if (cellEdit.item_id) {
        await api.put("/finance/cashflow/override", { week_start: cellEdit.week, item_id: cellEdit.item_id, amount });
      } else {
        await api.put("/finance/cashflow/ar", { week_start: cellEdit.week, amount: amount || 0 });
      }
      setCellEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const saveItem = async () => {
    if (!itemModal.name || !itemModal.amount) { toast.error("Name and amount required"); return; }
    try {
      await api.post("/finance/cashflow/items", { ...itemModal, amount: Number(itemModal.amount) });
      toast.success("Item saved");
      setItemModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const deactivateItem = async (it) => {
    if (!confirm(`Deactivate "${it.name}"? It will disappear from the forecast.`)) return;
    try {
      await api.post("/finance/cashflow/items", { ...it, active: false });
      toast.success(`${it.name} deactivated`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadAR = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: r } = await api.post("/finance/cashflow/ar-upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`MYOB AR imported — overdue ${fmt0(r.overdue)}, current ${fmt0(r.current_bucket)} spread over ${r.weeks_set} weeks`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!data) {
    return <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading cash flow…</div>;
  }

  const { config, rows, weeks } = data;
  const statutory = rows.filter((r) => r.category === "statutory");
  const regular = rows.filter((r) => r.category === "regular");
  const openingTotal = Number(config.opening_cheque) + Number(config.opening_term);

  const Cell = ({ row, cell }) => (
    <td className={`px-2 py-1 text-right tabular-nums cursor-pointer hover:bg-sky-50 ${cell.override ? "text-sky-700 font-semibold" : cell.amount ? "text-slate-700" : "text-slate-300"}`}
        data-testid={`cf-cell-${row.item_id}-${cell.week}`}
        title="Click to override this week"
        onClick={() => setCellEdit({ week: cell.week, item_id: row.item_id, value: cell.amount || "" })}>
      {cell.amount ? fmt0(cell.amount) : "—"}
    </td>
  );

  const Section = ({ label, items, subtotalKey }) => (
    <>
      <tr className="bg-slate-100/80 text-[10px] uppercase tracking-wider text-slate-500">
        <td className="px-3 py-1.5 sticky left-0 bg-slate-100/80 font-semibold" colSpan={1}>{label}</td>
        {weeks.map((w) => <td key={w.week}></td>)}
        <td></td>
      </tr>
      {items.map((r) => (
        <tr key={r.item_id} className="border-b border-slate-100 hover:bg-slate-50/40 group">
          <td className="px-3 py-1 sticky left-0 bg-white group-hover:bg-slate-50/40 whitespace-nowrap">
            <span className="text-slate-700">{r.name}</span>
            <span className="ml-1.5 text-[9px] uppercase text-slate-400">{r.frequency}</span>
          </td>
          {r.cells.map((c) => <Cell key={c.week} row={r} cell={c} />)}
          <td className="px-1 py-1 text-right whitespace-nowrap">
            <button className="text-slate-300 hover:text-slate-600 mr-1" title="Edit item"
                    data-testid={`cf-item-edit-${r.item_id}`}
                    onClick={() => setItemModal({ ...r, cells: undefined })}>
              <Pencil className="w-3 h-3" /></button>
            <button className="text-slate-300 hover:text-rose-600" title="Deactivate"
                    data-testid={`cf-item-del-${r.item_id}`}
                    onClick={() => deactivateItem({ ...r, cells: undefined })}>
              <X className="w-3 h-3" /></button>
          </td>
        </tr>
      ))}
      <tr className="border-b border-slate-200 bg-slate-50/60 font-semibold">
        <td className="px-3 py-1 sticky left-0 bg-slate-50/60 text-[11px]">Subtotal — {label}</td>
        {weeks.map((w, i) => (
          <td key={w.week} className="px-2 py-1 text-right tabular-nums text-[11px]">
            {fmt0(items.reduce((a, r) => a + r.cells[i].amount, 0))}
          </td>
        ))}
        <td></td>
      </tr>
    </>
  );

  return (
    <div className="space-y-4" data-testid="cashflow-panel">
      {/* Config strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Cheque account" val={fmt0(config.opening_cheque)} testid="cf-stat-cheque" />
        <Stat label="Term deposit" val={fmt0(config.opening_term)} testid="cf-stat-term" />
        <Stat label="Total opening cash" val={fmt0(openingTotal)} strong testid="cf-stat-total" />
        <Stat label="Minimum cash buffer" val={fmt0(config.min_cash)} tone="amber" testid="cf-stat-min" />
        <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col justify-center gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid="cf-edit-config"
                  onClick={() => setCfgEdit({ ...config })}>Edit balances</Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={load} disabled={busy}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Refresh
          </Button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <input type="file" accept=".xlsx" ref={fileRef} className="hidden"
               onChange={(e) => e.target.files?.[0] && uploadAR(e.target.files[0])} />
        <Button size="sm" className="bg-gbe hover:bg-gbe-dark text-white h-8 text-xs" disabled={uploading}
                data-testid="cf-upload-myob" onClick={() => fileRef.current?.click()}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
          Upload MYOB AR Report
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="cf-add-item"
                onClick={() => setItemModal({ name: "", category: "regular", amount: "", frequency: "monthly", active: true })}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add payment item
        </Button>
        <span className="text-[11px] text-slate-500 ml-2">
          Overdue AR spreads weeks 1–2 · current invoices weeks 2–5 · click any cell to override
        </span>
      </div>

      {/* Grid */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-xs min-w-[1400px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 whitespace-nowrap">Week commencing</th>
              {weeks.map((w, i) => (
                <th key={w.week} className="text-right px-2 py-2 whitespace-nowrap">
                  <div className="text-slate-400">Wk {i + 1}</div>
                  <div className="text-slate-700 font-semibold">{wkLabel(w.week)}</div>
                </th>
              ))}
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody data-testid="cf-grid-body">
            {/* AR collections */}
            <tr className="border-b border-slate-200 bg-emerald-50/40">
              <td className="px-3 py-1.5 sticky left-0 bg-emerald-50/40 font-semibold text-emerald-800 whitespace-nowrap">AR Collections (in)</td>
              {weeks.map((w) => (
                <td key={w.week}
                    className="px-2 py-1.5 text-right tabular-nums cursor-pointer hover:bg-emerald-100/60 font-semibold text-emerald-700"
                    data-testid={`cf-ar-${w.week}`}
                    title={w.ar_source ? `Source: ${w.ar_source}` : "Click to enter expected collections"}
                    onClick={() => setCellEdit({ week: w.week, item_id: null, value: w.inflow || "" })}>
                  {w.inflow ? fmt0(w.inflow) : <span className="text-emerald-300">—</span>}
                  {w.ar_source?.startsWith("myob") && <span className="block text-[8px] text-emerald-500 font-normal">MYOB</span>}
                </td>
              ))}
              <td></td>
            </tr>
            <Section label="Statutory payments" items={statutory} />
            <Section label="Regular payments" items={regular} />
            {/* Totals */}
            <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
              <td className="px-3 py-1.5 sticky left-0 bg-slate-50">Total outgoings</td>
              {weeks.map((w) => <td key={w.week} className="px-2 py-1.5 text-right tabular-nums text-rose-700">{fmt0(w.outflow)}</td>)}
              <td></td>
            </tr>
            <tr className="border-b border-slate-200 bg-slate-50 font-semibold">
              <td className="px-3 py-1.5 sticky left-0 bg-slate-50">Net movement</td>
              {weeks.map((w) => (
                <td key={w.week} className={`px-2 py-1.5 text-right tabular-nums ${w.net < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                  {fmt0(w.net)}
                </td>
              ))}
              <td></td>
            </tr>
            <tr className="bg-slate-800 text-white font-bold">
              <td className="px-3 py-2 sticky left-0 bg-slate-800 whitespace-nowrap">Closing balance</td>
              {weeks.map((w) => (
                <td key={w.week} data-testid={`cf-closing-${w.week}`}
                    className={`px-2 py-2 text-right tabular-nums ${w.below_min ? "bg-rose-600 text-white" : ""}`}
                    title={w.below_min ? "Below minimum cash buffer" : ""}>
                  {fmt0(w.closing)}
                </td>
              ))}
              <td className="bg-slate-800"></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Config modal */}
      {cfgEdit && (
        <Modal onClose={() => setCfgEdit(null)} title="Opening balances & cash buffer" testid="cf-config-modal">
          {[["opening_cheque", "Cheque account"], ["opening_term", "Term deposit"], ["min_cash", "Minimum cash buffer"]].map(([k, lbl]) => (
            <label key={k} className="block text-xs mb-3">
              <span className="text-slate-600">{lbl}</span>
              <Input type="number" step="0.01" value={cfgEdit[k]} data-testid={`cf-cfg-${k}`}
                     onChange={(e) => setCfgEdit({ ...cfgEdit, [k]: Number(e.target.value) })}
                     className="h-8 mt-1" />
            </label>
          ))}
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={() => setCfgEdit(null)}>Cancel</Button>
            <Button size="sm" className="bg-gbe hover:bg-gbe-dark text-white" onClick={saveCfg} data-testid="cf-cfg-save">Save</Button>
          </div>
        </Modal>
      )}

      {/* Cell override modal */}
      {cellEdit && (
        <Modal onClose={() => setCellEdit(null)} testid="cf-cell-modal"
               title={`${cellEdit.item_id ? "Override amount" : "AR collections"} — w/c ${wkLabel(cellEdit.week)}`}>
          <label className="block text-xs mb-3">
            <span className="text-slate-600">Amount for this week {cellEdit.item_id && "(leave blank to clear override)"}</span>
            <Input type="number" step="0.01" value={cellEdit.value} autoFocus data-testid="cf-cell-input"
                   onChange={(e) => setCellEdit({ ...cellEdit, value: e.target.value })}
                   onKeyDown={(e) => e.key === "Enter" && saveCell()}
                   className="h-8 mt-1" />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={() => setCellEdit(null)}>Cancel</Button>
            <Button size="sm" className="bg-gbe hover:bg-gbe-dark text-white" onClick={saveCell} data-testid="cf-cell-save">Save</Button>
          </div>
        </Modal>
      )}

      {/* Item modal */}
      {itemModal && (
        <Modal onClose={() => setItemModal(null)} title={itemModal.item_id ? "Edit payment item" : "Add payment item"} testid="cf-item-modal">
          <label className="block text-xs mb-3">
            <span className="text-slate-600">Name</span>
            <Input value={itemModal.name} data-testid="cf-item-name"
                   onChange={(e) => setItemModal({ ...itemModal, name: e.target.value })} className="h-8 mt-1" />
          </label>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className="block text-xs">
              <span className="text-slate-600">Amount</span>
              <Input type="number" step="0.01" value={itemModal.amount} data-testid="cf-item-amount"
                     onChange={(e) => setItemModal({ ...itemModal, amount: e.target.value })} className="h-8 mt-1" />
            </label>
            <label className="block text-xs">
              <span className="text-slate-600">Frequency</span>
              <Select value={itemModal.frequency} onValueChange={(v) => setItemModal({ ...itemModal, frequency: v })}>
                <SelectTrigger className="h-8 mt-1 text-xs" data-testid="cf-item-freq"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["weekly", "fortnightly", "monthly", "quarterly", "oneoff"].map((f) =>
                    <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
          </div>
          <label className="block text-xs mb-3">
            <span className="text-slate-600">Category</span>
            <Select value={itemModal.category} onValueChange={(v) => setItemModal({ ...itemModal, category: v })}>
              <SelectTrigger className="h-8 mt-1 text-xs" data-testid="cf-item-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="statutory">Statutory</SelectItem>
                <SelectItem value="regular">Regular</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={() => setItemModal(null)}>Cancel</Button>
            <Button size="sm" className="bg-gbe hover:bg-gbe-dark text-white" onClick={saveItem} data-testid="cf-item-save">Save item</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Stat({ label, val, sub, tone, strong, testid }) {
  return (
    <div data-testid={testid}
         className={`rounded-lg border p-3 ${tone === "amber" ? "border-amber-200 bg-amber-50/50" : "border-slate-200 bg-white"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`tabular-nums mt-1 ${strong ? "text-xl font-bold text-slate-900" : "text-lg font-semibold text-slate-800"}`}>{val}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-6"
         onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mt-16 p-5" data-testid={testid}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
